"use client";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "next/navigation";

interface DynamicSEOProps {
  pageType: "catalog" | "master" | "home" | "other";
  // For master
  masterName?: string;
  masterCity?: string;
  masterRating?: number;
  masterReviews?: number;
  masterTags?: string[];
  masterPhoto?: string;
  // For catalog
  city?: string;
  category?: string;
}

export function DynamicSEO({
  pageType,
  masterName,
  masterCity,
  masterRating,
  masterReviews,
  masterTags,
  masterPhoto,
  city,
  category,
}: DynamicSEOProps) {
  const { t, i18n } = useTranslation("common");
  const searchParams = useSearchParams();

  useEffect(() => {
    // Determine language from URL or fallback
    const lang = searchParams.get("lang") || i18n.language || "he";
    const isHebrew = lang === "he";

    /* Запасные заголовок и описание — на иврите, а не по-английски.
       Их видит тот, у кого не определился тип страницы, и именно они уходили
       в превью маршрута `/master/?id=…&book=1` вместе с «Premium Beauty
       Marketplace». Площадка израильская, язык по умолчанию — иврит. */
    let title = "YofiPlace — הפלטפורמה הישראלית להזמנת תורים אצל מומחי יופי";
    let description = "מניקור, תספורות, איפור, עיסוי — תור אונליין בכמה נגיעות, בלי טלפונים.";

    if (pageType === "master" && masterName) {
      if (isHebrew) {
        title = `${masterName} - ${masterCity ? masterCity : ""} | YofiPlace`;
        description = `${masterName} - ${masterTags?.join(", ") || "שירותי יופי"}. קרא חוות דעת, בדוק מחירים והזמן תור עכשיו.`;
      } else if (lang === "ru") {
        title = `${masterName} - мастера красоты в ${masterCity ? masterCity : "Израиле"} | YofiPlace`; // город приходит уже в нужном падеже (K-0069)
        description = `${masterName} - ${masterTags?.join(", ") || "услуги красоты"}. Читайте отзывы, смотрите цены и записывайтесь онлайн.`;
      } else {
        title = `${masterName} - Beauty Services in ${masterCity ? masterCity : "Israel"} | YofiPlace`;
        description = `Book ${masterName} for ${masterTags?.join(", ") || "beauty services"}. Read reviews and check availability.`;
      }
    } else if (pageType === "catalog") {
      const cityKey = city ? city.replace("-", " ") : "";
      const catKey = category ? t(`categories.${category}`) : "";
      
      if (isHebrew) {
        title = `${catKey ? catKey + " " : ""}${cityKey ? "ב" + cityKey : "חיפוש מומחים"} | YofiPlace`;
        description = `מצא את שירותי ה${catKey || "יופי"} הטובים ביותר ${cityKey ? "ב" + cityKey : "בישראל"}. השווה מחירים וקרא חוות דעת.`;
      } else if (lang === "ru") {
        title = `${catKey ? catKey + " " : ""}${cityKey ? "в " + cityKey : "Поиск мастеров"} | YofiPlace`;
        description = `Найдите лучших мастеров ${catKey || "красоты"} ${cityKey ? "в " + cityKey : "в Израиле"}. Сравните цены и отзывы.`;
      } else {
        title = `Best ${catKey || "Beauty Masters"} ${cityKey ? "in " + cityKey : ""} | YofiPlace`;
        description = `Find and book top ${catKey || "beauty services"} ${cityKey ? "in " + cityKey : "in Israel"}. Read real reviews.`;
      }
    }

    // Apply title and description dynamically
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", description);
    }

    // JSON-LD Structured Data Lifecycle
    const schemaId = "yofiplace-json-ld";
    const scriptEl = document.getElementById(schemaId);

    if (scriptEl) {
      scriptEl.remove();
    }

    if (pageType === "master" && masterName) {
      const schemaData = {
        "@context": "https://schema.org",
        "@type": "HealthAndBeautyBusiness",
        "name": masterName,
        "image": masterPhoto || "https://yofiplace.co.il/icons/icon-512.png",
        "@id": window.location.href,
        "url": window.location.href,
        "telephone": "",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": masterCity || "Israel",
          "addressCountry": "IL"
        },
        "aggregateRating": masterRating && masterReviews ? {
          "@type": "AggregateRating",
          "ratingValue": masterRating.toString(),
          "reviewCount": masterReviews.toString()
        } : undefined
      };

      const script = document.createElement("script");
      script.id = schemaId;
      script.type = "application/ld+json";
      script.text = JSON.stringify(schemaData);
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup on unmount
      const existing = document.getElementById(schemaId);
      if (existing) existing.remove();
    };
  }, [pageType, masterName, masterCity, masterRating, masterReviews, masterTags, masterPhoto, city, category, i18n.language, searchParams, t]);

  return null;
}
